import React from 'react';
import { FileEdit } from 'lucide-react';
import type { RenameOptions } from '../types';

interface RenameControlsProps {
  options: RenameOptions;
  onChange: (options: RenameOptions) => void;
  imageCount: number;
}

const RenameControls: React.FC<RenameControlsProps> = ({ options, onChange, imageCount }) => {
  const padDigits = Math.max(2, String(imageCount).length);
  const exampleNum = '1'.padStart(padDigits, '0');
  const pattern = options.pattern.trim() || 'image';

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-cyan-900/30 border border-cyan-500/20 flex items-center justify-center">
          <FileEdit size={16} className="text-cyan-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-200">Rename Files</h3>
        <label className="ml-auto flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-slate-400">{options.enabled ? 'On' : 'Off'}</span>
          <button
            onClick={() => onChange({ ...options, enabled: !options.enabled })}
            className={`relative w-10 h-5 rounded-full transition-colors ${options.enabled ? 'bg-cyan-600' : 'bg-slate-700'}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${options.enabled ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </label>
      </div>

      {options.enabled && (
        <div className="space-y-3 animate-fade-in">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">Naming pattern</label>
            <input
              type="text"
              value={options.pattern}
              onChange={(e) => onChange({ ...options, pattern: e.target.value })}
              placeholder="e.g. property-bedroom"
              className="w-full px-3 py-2.5 bg-black border border-slate-700 rounded-xl text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all"
            />
          </div>

          <div className="bg-slate-800/60 border border-slate-700/50 rounded-lg p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Preview</p>
            <div className="space-y-0.5 text-xs text-slate-300 font-mono">
              <p>{pattern}-{exampleNum}.jpg</p>
              <p>{pattern}-{'2'.padStart(padDigits, '0')}.jpg</p>
              {imageCount > 2 && <p>{pattern}-{'3'.padStart(padDigits, '0')}.jpg</p>}
              {imageCount > 3 && <p className="text-slate-500">...</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenameControls;
